import { useCallback, useEffect, useState, useRef } from 'react';
import { useVoiceStore } from '@stores/voiceStore';
import { getCurrentLanguage } from '@/i18n';
import { isSpeechRecognitionSupported } from './recognition';

/**
 * Transcript result from speech recognition
 */
export interface TranscriptResult {
  text: string;
  isFinal: boolean;
  confidence?: number;
}

/**
 * Get the SpeechRecognition constructor (vendor-prefixed in some browsers)
 */
function getSpeechRecognition() {
  if (typeof window === 'undefined') {
    return null;
  }

  // @ts-expect-error - webkitSpeechRecognition not in TypeScript types
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * Map locale codes to speech recognition language codes
 */
function mapLocaleToRecognitionLang(locale: string): string {
  const map: Record<string, string> = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    de: 'de-DE',
  };
  return map[locale] || 'en-US';
}

/**
 * Get error message for speech recognition error
 */
function getErrorMessage(errorCode: string): string {
  const messages: Record<string, string> = {
    'no-speech': 'No speech was detected. Please try again.',
    'audio-capture': 'Microphone not available. Please check your device.',
    'not-allowed': 'Microphone permission denied. Please allow microphone access.',
    network: 'Network error occurred. Please check your connection.',
    aborted: 'Speech recognition was aborted.',
    'language-not-supported': 'Language not supported.',
    'service-not-allowed': 'Speech recognition service not allowed.',
  };

  return messages[errorCode] || `Speech recognition error: ${errorCode}`;
}

/**
 * React hook for speech-to-text functionality using Web Speech API
 */
export function useSpeechToText() {
  const { isListening, setIsListening } = useVoiceStore();
  const [transcript, setTranscript] = useState<TranscriptResult>({ text: '', isFinal: false });
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef('');
  const interimTranscriptRef = useRef('');

  // Check if speech recognition is supported
  useEffect(() => {
    setIsSupported(isSpeechRecognitionSupported());
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) {
      return;
    }

    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      const locale = getCurrentLanguage();

      recognition.lang = mapLocaleToRecognitionLang(locale);
      recognition.continuous = false; // Stop after one utterance
      recognition.interimResults = true; // Show real-time transcription
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setError(null);
        finalTranscriptRef.current = '';
        interimTranscriptRef.current = '';
      };

      recognition.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcriptText = result[0].transcript;

          if (result.isFinal) {
            finalTranscript += transcriptText;
          } else {
            interimTranscript += transcriptText;
          }
        }

        // Update refs
        if (finalTranscript) {
          finalTranscriptRef.current += finalTranscript;
        }
        interimTranscriptRef.current = interimTranscript;

        // Update state with combined transcript
        const combinedText = finalTranscriptRef.current + interimTranscriptRef.current;
        setTranscript({
          text: combinedText,
          isFinal: false,
          confidence: event.results[event.results.length - 1][0].confidence,
        });
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setError(getErrorMessage(event.error));
        setIsListening(false);
      };

      recognition.onend = () => {
        // Mark final transcript
        if (finalTranscriptRef.current) {
          setTranscript({
            text: finalTranscriptRef.current,
            isFinal: true,
          });
        }
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } catch (err) {
      console.error('Failed to initialize speech recognition:', err);
      setError('Failed to initialize speech recognition');
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // Ignore errors during cleanup
        }
      }
    };
  }, [isSupported, setIsListening]);

  /**
   * Start listening for voice input
   */
  const startListening = useCallback(async () => {
    if (!isSupported) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    if (!recognitionRef.current) {
      setError('Speech recognition not initialized');
      return;
    }

    if (isListening) {
      return; // Already listening
    }

    try {
      // Reset state
      setError(null);
      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
      setTranscript({ text: '', isFinal: false });

      // Start recognition
      recognitionRef.current.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setError('Failed to start speech recognition');
    }
  }, [isSupported, isListening]);

  /**
   * Stop listening and finalize transcript
   */
  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) {
      return;
    }

    try {
      recognitionRef.current.stop();
    } catch (err) {
      console.error('Failed to stop speech recognition:', err);
    }
  }, [isListening]);

  /**
   * Abort listening immediately
   */
  const abortListening = useCallback(() => {
    if (!recognitionRef.current) {
      return;
    }

    try {
      recognitionRef.current.abort();
      setIsListening(false);
      setTranscript({ text: '', isFinal: false });
      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
    } catch (err) {
      console.error('Failed to abort speech recognition:', err);
    }
  }, [setIsListening]);

  /**
   * Clear transcript
   */
  const clearTranscript = useCallback(() => {
    setTranscript({ text: '', isFinal: false });
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    setError(null);
  }, []);

  return {
    startListening,
    stopListening,
    abortListening,
    clearTranscript,
    isListening,
    transcript,
    error,
    isSupported,
  };
}

/**
 * Request microphone permission
 * Returns true if permission granted, false otherwise
 */
export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Stop the stream immediately (we just wanted to check permission)
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    console.error('Microphone permission denied:', error);
    return false;
  }
}

/**
 * Check if microphone permission is granted
 */
export async function checkMicrophonePermission(): Promise<PermissionState> {
  try {
    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return result.state;
  } catch {
    // Fallback: try to access microphone
    const granted = await requestMicrophonePermission();
    return granted ? 'granted' : 'denied';
  }
}
